import { API_PATHS } from "../constants/api-paths";
import { AddOnProd } from "../types/sso.types";
import { cryptoAPICalledData, decryptAESCBC256 } from "@repo/shared-utils/crypto";
import { getCookie, getParams } from "@repo/shared-utils/common-utils";
import { type IResponse, encryptedRequest } from "../core/api.client";

export interface IAppDtls {
  pan: string;
  dob: string;
  employmentType?: string;
  fullName: string;
  gender: string;
  kycDtls: {
    address_pin: string;
    address_city: string;
  };
  gstInNo: string;
  monthlyIncome?: string;
}

export interface ISalPLAdditionalPayload {
  id?: string;
  journeyName?: string;
  eventCode?: string;
  mcpFlag?: string;
  sourcingBranchId?: string;
  revisedOfferAmt?: string;
  offerId?: string;
  tncFlag?: string;
  srcChannelCategory?: string;
  pqOfferRoiAnnual?: string;
  offerProduct?: string;
  pwaVersion?: string;
  nativeVersion?: string;
  requestSource?: string;
  srcChannel?: string;
  apiTimeout?: string;
  appDtls?: IAppDtls[];
  processType?: string;
  processSegmentation?: string;
  journeyType?: string;
  appVersion?: string;
  cta?: string;
  utmSource?: string;
  utmContent?: string;
  utmCampaign?: string;
  utmMedium?: string;
  utmTerm?: string;
  sourceUrl?: string;
  offerName?: string;
  loanType?: string;
}

export interface IPlcsInsertApiRequest {
  mobileNo: string;
  journeyName: string;
  processType?: string;
  processSegmentation?: string;
  sourcingBranchId?: string;
  requestedLoanAmt: string;
  loanVariant: string;
  requestedLoanTenor: string;
  hybridTenor?: string;
  dropLineTenor?: string;
  hybridEMIAmt?: string;
  droplineEMIAmt?: string;
  emiAmt?: string;
  requestedIRR: string;
  requestedROI: string;
  callbackRequested: boolean;
  offerId?: string;
  appDtls?: IAppDtls[];
  pqOfferRoiAnnual?: string;
  pqOfferIrr: string;
  startNewApplication: boolean;
  journeyType?: string;
  addOnProd?: AddOnProd[];
  offerProduct?: string;
  appId?: string;
  id?: string;
  reqSrc?: string;
  partnerBusiness?: string;
  appVersion?: string;
}

export interface IPlcsInsertApiResponse {
  id: string;
  eventCode: string;
  statusCode: string;
  description?: string;
  offerDisposition: string;
  nameMatchRetryCounter: string;
}
export interface IUtmParams {
  utmCampaign?: string;
  utmMedium?: string;
  utmSource?: string;
  utmTerm?: string;
}

export type ISalplInsertApiRequest = {
  mobileNo: string;
  journeyName: string;
  sourcingBranchId?: string;
  requestedLoanAmt: string;
  loanVariant: string;
  requestedLoanTenor: string;
  hybridTenor?: string;
  dropLineTenor?: string;
  hybridEMIAmt?: string;
  droplineEMIAmt?: string;
  emiAmt?: string;
  callbackRequested: boolean;
  requestedIRR: string;
  requestedROI: string;
  offerId?: string;
  appDtls?: IAppDtls[];
  pqOfferRoiAnnual?: string;
  pqOfferIrr: string;
  addOnProd?: AddOnProd[];
  offerProduct?: string;
  appId?: string;
  reqSrc?: string;
  partnerBusiness?: string;
  appVersion?: string;
};

export type ISalplInsertApiResponse = {
  id: string;
  eventCode: string;
  description?: string;
  statusCode: string;
  offerProduct?: string;
  txId: string;
  nameMatchRetryCounter: string;
  cibilScore: string;
};

export const getM1TrackingParams = () => {
  const urlParams = new URLSearchParams(globalThis.window.location.search);
  const utmSource = urlParams.get("utm_source");

  if (utmSource?.toLowerCase() === "m1") {
    return {
      sourceSystem: "M1",
      srcChannel: "M1",
    };
  }

  return {};
};

export const setUtmParamsForInsert = async () => {
  try {
    const campaignCookie = getCookie("campaignCookie")
      ? JSON.parse(getCookie("campaignCookie"))
      : {};
    const utmSource = getParams("utm_source") || campaignCookie.utm_source;
    const utmCampaign = getParams("utm_campaign") || campaignCookie.utm_campaign;
    let utmMedium = getParams("utm_medium") || campaignCookie.utm_medium;
    const utmTerm = getParams("utm_term") || campaignCookie.utm_term;
    if (utmSource?.toLowerCase() === "referandearn" && utmMedium) {
      const { REQ_BFL_REFER_KEY, REQ_BFL_REFER_IV } = await cryptoAPICalledData();
      if (REQ_BFL_REFER_KEY && REQ_BFL_REFER_IV) {
        utmMedium = await decryptAESCBC256(utmMedium, REQ_BFL_REFER_KEY, REQ_BFL_REFER_IV);
      }
    }
    return {
      utm_source: utmSource ?? "NA",
      utm_campaign: utmCampaign ?? "NA",
      utm_medium: utmMedium ?? "NA",
      utm_term: utmTerm ?? "NA",
    };
  } catch (error) {
    console.error("setUtmParamsForInsert failed:", error);
    return {};
  }
};

export const postPlcsInsert = async (
  plcsInsertRequestData: IPlcsInsertApiRequest
): Promise<IResponse<IPlcsInsertApiResponse>> => {
  const response = await encryptedRequest<IPlcsInsertApiResponse, IPlcsInsertApiRequest>({
    method: "POST",
    url: API_PATHS.plcsInsert,
    data: {
      ...plcsInsertRequestData,
      appVersion: "v5",
      ...(await setUtmParamsForInsert()),
      ...getM1TrackingParams(),
    },
  });

  return response;
};

export const postSalplInsert = async (
  salplInsertRequestData: ISalplInsertApiRequest
): Promise<IResponse<ISalplInsertApiResponse>> => {
  const response = await encryptedRequest<ISalplInsertApiResponse, ISalplInsertApiRequest>({
    method: "POST",
    url: API_PATHS.salplInsert,
    data: {
      ...salplInsertRequestData,
      appVersion: "v5",
      ...(await setUtmParamsForInsert()),
      ...getM1TrackingParams(),
    },
  });

  return response;
};
